import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, firestore, analytics } from "../firebase.js";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, collection, getDocs, setDoc } from "firebase/firestore";
import Logo from "../assets/DogTrainerAI.png";
import { logEvent } from "../utils/analyticsUtils.js";

const NavBar = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dogImage, setDogImage] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasPlan, setHasPlan] = useState(false);
  const [hasPurchases, setHasPurchases] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setDogImage(null);
        setHasPlan(false);
        setHasPurchases(false);
        setIsSubscribed(false);
        setSubscriptionPlan(null);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const userDocRef = doc(firestore, "users", currentUser.uid);
    const unsubscribeUserDoc = onSnapshot(
      userDocRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setHasPlan(data.hasPersonalizedPlan || false);
          setIsSubscribed(data.isSubscribed || false);
          setSubscriptionPlan(data.subscriptionPlan || null);
        } else {
          setHasPlan(false);
          setIsSubscribed(false);
          setSubscriptionPlan(null);
        }
      },
      (error) => {
        console.error("NavBar.js: Error listening for user data:", error.message);
        setHasPlan(false);
        setIsSubscribed(false);
        setSubscriptionPlan(null);
      }
    );

    const purchasesRef = collection(firestore, "users", currentUser.uid, "purchases");
    const unsubscribePurchases = onSnapshot(
      purchasesRef,
      (snapshot) => {
        console.log("NavBar.js: Purchases snapshot:", {
          totalDocs: snapshot.docs.length,
          docIds: snapshot.docs.map(doc => doc.id),
          empty: snapshot.empty
        });
        
        setHasPurchases(!snapshot.empty);
      },
      (error) => {
        console.error("NavBar.js: Error listening for purchases:", error.message);
        setHasPurchases(false);
      }
    );

    const dogsRef = collection(firestore, "users", currentUser.uid, "dogs");
    getDocs(dogsRef)
      .then((dogsSnapshot) => {
        if (!dogsSnapshot.empty) {
          const firstDogId = dogsSnapshot.docs[0].id;
          const dogDocRef = doc(firestore, "users", currentUser.uid, "dogs", firstDogId);
          const unsubscribeDogDoc = onSnapshot(
            dogDocRef,
            (doc) => {
              if (doc.exists()) {
                const data = doc.data();
                setDogImage(data.dogImage || null);
              } else {
                setDogImage(null);
              }
            },
            (error) => {
              console.error("NavBar.js: Error listening for dog image:", error.message);
              setDogImage(null);
            }
          );
          return unsubscribeDogDoc;
        } else {
          console.log("NavBar.js: No dog documents found, setting dog image to null");
          setDogImage(null);
        }
      })
      .catch((error) => {
        console.error("NavBar.js: Error fetching dog data:", {
          message: error.message,
          code: error.code,
          stack: error.stack,
        });
        setDogImage(null);
      });

    return () => {
      unsubscribeUserDoc();
      unsubscribePurchases();
    };
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleEditProfile = () => {
    setDropdownOpen(false);
    navigate("/edit-profile");
  };

  const handlePlanClick = () => {
    logEvent(analytics, "navbar_plan_click", {
      user_id: currentUser?.uid || "anonymous",
      source: "dropdown",
    });
    setDropdownOpen(false);
    navigate("/personalized-plan");
  };

  const handlePurchasesClick = () => {
    logEvent(analytics, "navbar_purchases_click", {
      user_id: currentUser?.uid || "anonymous",
      source: "dropdown",
    });
    setDropdownOpen(false);
    navigate("/purchases");
  };

  const handleMembershipClick = () => {
    logEvent(analytics, "navbar_membership_click", {
      user_id: currentUser?.uid || "anonymous",
      source: "dropdown",
      subscription_plan: subscriptionPlan,
    });
    setDropdownOpen(false);
    navigate("/my-membership");
  };

  // Debug logging
  console.log("NavBar.js: Render state:", {
    isSubscribed,
    subscriptionPlan,
    hasPurchases,
    hasPlan,
    dropdownOpen
  });

  return (
    <nav style={styles.navbar} role="navigation">
      <div style={styles.contentContainer}>
        <button
          style={styles.logoButton}
          onClick={() => navigate("/")}
          aria-label="Go to Home"
        >
          <img src={Logo} style={styles.logo} alt="DoglingoAI Logo" />
          <span
            style={{
              ...styles.logoText,
              fontSize: isMobile ? "24px" : "32px",
            }}
          >
            Doglingo
          </span>
        </button>

        <div style={isMobile ? styles.navLinksMobile : styles.navLinks}>
          {currentUser && (
            <button
              style={styles.navLinkContainer}
              onClick={() => navigate("/dashboard")}
              aria-label="Dashboard"
            >
              <span style={styles.navLink}>Dashboard</span>
            </button>
          )}
          {!isMobile && (
            <button
              style={styles.navLinkContainer}
              onClick={() => navigate("/challenges")}
              aria-label="Challenges"
            >
              <span style={styles.navLink}>Challenges</span>
            </button>
          )}
          {!isMobile && (
            <button
              style={styles.navLinkContainer}
              onClick={() => navigate(currentUser ? "/skills" : "/onboarding")}
              aria-label={currentUser ? "Train" : "Start"}
            >
              <span style={styles.navLink}>{currentUser ? "Train" : "Start"}</span>
            </button>
          )}
          {currentUser ? (
            <>
              <button
                style={styles.profileCircle}
                onClick={toggleDropdown}
                aria-label="User Profile"
              >
                <img
                  src={dogImage || "https://placehold.co/40x40?text=Dog"}
                  style={styles.profileImage}
                  alt="Dog Profile"
                />
              </button>
              {dropdownOpen && (
                <div
                  ref={dropdownRef}
                  style={isMobile ? styles.dropdownMobile : styles.dropdown}
                >
                  <button
                    className="doglingo-dropdown-item"
                    onClick={handleEditProfile}
                    aria-label="Edit Profile"
                  >
                    Edit Profile
                  </button>
                  {hasPlan && (
                    <button
                      className="doglingo-dropdown-item"
                      onClick={handlePlanClick}
                      aria-label="My Plan"
                    >
                      My Plan
                    </button>
                  )}
                  {hasPurchases && (
                    <button
                      className="doglingo-dropdown-item"
                      onClick={handlePurchasesClick}
                      aria-label="Purchases"
                    >
                      Purchases
                    </button>
                  )}
                  {isSubscribed && (
                    <button
                      className="doglingo-dropdown-item"
                      onClick={handleMembershipClick}
                      aria-label="My Membership"
                    >
                      My Membership
                    </button>
                  )}
                  <button
                    className="doglingo-dropdown-item"
                    onClick={handleLogout}
                    aria-label="Logout"
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <button
              style={styles.navLinkContainer}
              onClick={() => navigate("/login")}
              aria-label="Login"
            >
              <span style={styles.navLink}>Login</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

const styles = {
  navbar: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 20px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #E2E8F0",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
    width: "100%",
  },
  contentContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "1200px",
  },
  logoButton: {
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  logo: {
    width: "50px",
    height: "auto",
    objectFit: "contain",
  },
  logoText: {
    fontFamily: "MouseMemoirs, sans-serif",
    color: "#2D3748",
    marginLeft: "10px",
    fontWeight: "600",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    position: "relative",
  },
  navLinksMobile: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "0px",
    paddingLeft: "0px",
    position: "relative",
  },
  navLinkContainer: {
    padding: "8px 12px",
    paddingLeft: "5px",
    borderRadius: "8px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "background-color 0.2s, color 0.2s",
  },
  navLink: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#38A169",
    textDecoration: "none",
    whiteSpace: "nowrap",
    fontFamily: "MouseMemoirs, sans-serif",
  },
  profileCircle: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid #E2E8F0",
    cursor: "pointer",
    background: "none",
    padding: 0,
  },
  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  dropdown: {
    position: "absolute",
    top: "50px",
    right: 0,
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 1001,
    width: "150px",
    textAlign: "left",
  },
  dropdownMobile: {
    position: "fixed",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 1001,
    width: "120px",
    textAlign: "left",
  },
  dropdownItem: {
    display: "block",
    width: "100%",
    padding: "10px 15px",
    backgroundColor: "transparent",
    border: "none",
    fontSize: "16px",
    color: "#38A169",
    textAlign: "left",
    cursor: "pointer",
    transition: "background-color 0.2s",
    fontFamily: "MouseMemoirs, sans-serif",
  },
};

export default NavBar;